import { ArrowLeft, Calendar, Clock, Share2 } from 'lucide-react';
import { marked } from 'marked';
import { useEffect } from 'react';
import { updatePageMeta, addStructuredData, generateBlogPostStructuredData } from '../../utils/seo';

marked.setOptions({
  breaks: true,
  gfm: true,
  headerIds: true,
  mangle: false,
  sanitize: false,
});

const post = {
  "slug": "eid-gift-giving-traditions",
  "title": "Eid Gift-Giving: Celebrating Eid al-Fitr and Eid al-Adha",
  "excerpt": "Learn about Eidi traditions, appropriate gifts, and thoughtful ways to celebrate the two Eid festivals with family and community.",
  "date": "2025-03-20",
  "readTime": "6 min read",
  "category": "Festivals",
  "image": "🌙",
  "heroImage": "https://images.unsplash.com/photo-1589462945217-c8a47e07caaf?w=1200&h=600&fit=crop",
  "author": "SwapGifts Editorial Team",
  "content": "# Eid Gift-Giving: Celebrating Eid al-Fitr and Eid al-Adha\n\nEid celebrations, marking the end of Ramadan (Eid al-Fitr) and honoring Prophet Ibrahim's sacrifice (Eid al-Adha), include beautiful traditions of generosity and gift-giving.\n\n## The Tradition of Eidi\n\nEidi is the tradition of giving money or gifts, especially to children and young people.\n\n**Who Gives Eidi**:\n- Elders to younger family members\n- Employers to employees\n- Wealthy community members to those in need\n\n**Typical Amounts**:\n- Children: $5-$20\n- Teenagers: $20-$50\n- Young adults: $50-$100\n- Household staff: One month's salary or equivalent\n\n## Traditional Eid Gifts\n\n**For Children**:\n- New clothes (traditional or modern)\n- Toys and games\n- Books\n- Money in decorative envelopes\n- Sweets and treats\n\n**For Adults**:\n- Dates and dried fruits\n- Perfumes (attar or modern fragrances)\n- Prayer items (prayer rugs, tasbeeh)\n- Islamic art or calligraphy\n- Gift baskets with treats\n\n## Modern Gift Ideas\n\n**For Family**:\n- Jewelry\n- Home decor\n- Kitchen appliances\n- Tech gadgets\n- Gift cards\n\n**For Friends**:\n- Gourmet food baskets\n- Scented candles\n- Books\n- Board games\n- Plant or flowers\n\n**Experience Gifts**:\n- Restaurant vouchers\n- Amusement park tickets\n- Spa treatments\n- Online course subscriptions\n\n## Gift-Giving Etiquette\n\n**Timing**:\n- Exchange after Eid prayers\n- Throughout the three days of celebration\n- When visiting family and friends\n\n**Presentation**:\n- Wrap in festive paper or gift bags\n- Use gold, green, or metallic colors\n- Include an Eid greeting card\n- Give with the right hand (Islamic tradition)\n\n## Charitable Giving\n\nZakat al-Fitr and Sadaqah are essential parts of Eid:\n\n**Before Eid al-Fitr**:\n- Give Zakat al-Fitr (obligatory charity before Eid prayers)\n- Typically equivalent to 3-4 kg of staple food or monetary value\n\n**During Both Eids**:\n- Donate to charitable causes\n- Give to those in need in the community\n- Support local Islamic organizations\n\n## Organizing an Eid Gift Exchange\n\nFor large families or community groups:\n\n**Secret Eid Gift Exchange**:\n- Set budget at $25-50\n- Draw names after Ramadan begins\n- Exchange at Eid gathering\n- Focus on halal, appropriate gifts\n\n**Children's Eid Party**:\n- Organize games with small prizes\n- Goodie bags for all attendees\n- Craft activities\n- Storytime about Eid traditions\n\n## What to Avoid\n\n- Alcohol\n- Pork products\n- Inappropriate imagery or items\n- Gambling-related items\n- Items depicting animate objects (for some conservative families)\n\n## Making Eid Special\n\nBeyond material gifts:\n- Spend quality time with family\n- Forgive past grievances\n- Reconnect with distant relatives\n- Share meals together\n- Pray together\n- Share stories and laughter\n\n## Conclusion\n\nEid gift-giving reflects the Islamic values of generosity, community, and joy. Whether through Eidi, thoughtful presents, or charitable donations, the spirit of giving makes Eid truly special."
};

export default function EidGiftGivingTraditionsPage() {
  useEffect(() => {
    updatePageMeta({
      title: `${post.title} | SwapGifts Blog`,
      description: post.excerpt,
      ogTitle: post.title,
      ogDescription: post.excerpt,
      ogImage: post.heroImage || 'https://swapgifts.store/default-blog-image.jpg',
      canonicalUrl: `https://swapgifts.store/blog/${post.slug}`,
      keywords: `gift exchange, ${post.category.toLowerCase()}, secret santa, ${post.title.toLowerCase()}`
    });

    addStructuredData(generateBlogPostStructuredData({
      title: post.title,
      excerpt: post.excerpt,
      date: post.date,
      author: post.author || 'SwapGifts Editorial Team',
      heroImage: post.heroImage,
      slug: post.slug
    }));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <a
          href="/blog"
          className="inline-flex items-center gap-2 text-green-600 font-medium hover:underline mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </a>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {post.heroImage && (
            <div className="w-full h-96 overflow-hidden">
              <img
                src={post.heroImage}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8 md:p-12">
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                {post.category}
              </span>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {post.title}
            </h1>

            {post.author && (
              <p className="text-sm text-gray-500 mb-6">
                By <span className="font-medium text-gray-700">{post.author}</span>
              </p>
            )}

            <p className="text-xl text-gray-600 mb-8 leading-relaxed border-b border-gray-200 pb-8">
              {post.excerpt}
            </p>

            <div className="flex items-center gap-4 pb-8 mb-8 border-b border-gray-200">
              <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>

            <div
              className="prose prose-lg max-w-none
                prose-headings:font-bold prose-headings:text-gray-900 prose-headings:mt-8 prose-headings:mb-4
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
                prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
                prose-a:text-green-600 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-gray-900 prose-strong:font-semibold
                prose-ul:my-6 prose-ul:space-y-2
                prose-ol:my-6 prose-ol:space-y-2
                prose-li:text-gray-700 prose-li:leading-relaxed
                prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm"
              dangerouslySetInnerHTML={{ __html: marked.parse(post.content) }}
            />
          </div>

          <div className="mt-12 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to organize your gift exchange?</h2>
            <p className="text-green-100 mb-6">
              Put these tips into action and create your exchange in minutes
            </p>
            <a
              href="/create"
              className="inline-block px-8 py-3 bg-white text-green-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Start Drawing Names
            </a>
          </div>
        </div>
      </article>
    </div>
  );
}
